import type {
  GTMEventGlycerinCampaignClick,
  GTMEventGlycerinDisplayMarketing,
  GTMEventGlycerinMarketingClick,
  GTMEventGlycerinDisplayCampaign,
  GenericTrackingEvent,
} from '@packages/tracking/src/types/events';
import { useTracking } from '@packages/tracking/src/hooks/useTracking/useTracking';
import type { RefObject } from 'react';
import config from '@packages/config';
import { GlycerinMarketingType } from '@packages/tracking/src/types/enums';
import { useIntersectionObserver } from '../useIntersectionObserver/useIntersectionObserver';
import { useI18n } from '../useI18n/useI18n';

type BehaviorProps = {
  behavior_paragraph_marketing_display_type?: 'campaign' | 'ad';
  behavior_paragraph_campaign_type?: string | null;
  behavior_paragraph_campaignid?: string | null;
  // for flyout-menu and usp-bar the campaign-id is underscored
  behavior_paragraph_campaign_id?: string | null;
  behavior_paragraph_category?: string | null;
  behavior_paragraph_detail?: string | null;
  behavior_paragraph_name?: string | null;
};

type PromotionProps = {
  glycerinCampaignType?: string;
  glycerinCampaignId?: string;
  glycerinAdType?: string;
  glycerinAdId?: string;
  glycerinCategory?: string;
  glycerinDetail?: string;
  glycerinName?: string;
  linkReady?: string;
};

type GlycerinCampaignTracking = BehaviorProps | PromotionProps;

type FinalTrackingData = {
  category?: string;
  name?: string;
  trackingTarget?: string;
  detail?: string;
  campaignType?: string;
  campaignId?: string;
  adType?: string;
  adId?: string;
};

const generateFinalTrackingData = (data: GlycerinCampaignTracking, target: string | undefined) => {
  let finalTrackingData: FinalTrackingData = {};

  // data from Drupal
  if ('behavior_paragraph_campaign_type' in data) {
    const isAd = data.behavior_paragraph_marketing_display_type === 'ad';
    const isCampaign = !isAd; // for campaigns behavior_paragraph_marketing_display_type should be "campaign", but for backwards compatibility, every marketing that is not an ad, is a campaign

    finalTrackingData = {
      category: data.behavior_paragraph_category || undefined,
      name: data.behavior_paragraph_name || undefined,
      trackingTarget: target,
      detail: data.behavior_paragraph_detail || undefined,
      ...(isAd && {
        adType: data.behavior_paragraph_campaign_type || undefined, // if this is used as ad, use the behavior_paragraph_campaign_type as ad campaign type
        adId:
          data.behavior_paragraph_campaignid || data.behavior_paragraph_campaign_id || undefined, // if this is used as ad, use the behavior_paragraph_campaignid as ad campaign id
      }),
      ...(isCampaign && {
        campaignType: data.behavior_paragraph_campaign_type || undefined,
        campaignId:
          data.behavior_paragraph_campaignid || data.behavior_paragraph_campaign_id || undefined,
      }),
    };
  } else if ('glycerinCampaignType' in data || 'glycerinAdType' in data) {
    const isAd = data.glycerinAdId && data.glycerinAdType;
    const isCampaign = !isAd; // for backwards compatibility, every marketing that is not an ad, is a campaign

    finalTrackingData = {
      category: data.glycerinCategory,
      name: data.glycerinName,
      trackingTarget: data.linkReady,
      detail: data.glycerinDetail,
      ...(isAd && {
        adType: data.glycerinAdType,
        adId: data.glycerinAdId,
      }),
      ...(isCampaign && {
        campaignType: data.glycerinCampaignType,
        campaignId: data.glycerinCampaignId,
      }),
    };
  }
  return finalTrackingData;
};

const useReturnOutput = (
  data: GlycerinCampaignTracking,
  options: { trackMarketing?: boolean; target?: string } = {},
) => {
  const { language, defaultLanguage } = useI18n();
  const basePath = config.headerInformation?.de?.canonical ?? '';
  const dispatchGtmEvent = useTracking();

  const { campaignType, campaignId, adId, adType, category, name, trackingTarget, detail } =
    generateFinalTrackingData(data, options.target);

  if (category) {
    let targetUrl: string | undefined;
    try {
      targetUrl = trackingTarget && new URL(trackingTarget, basePath).toString();
    } catch {
      // do nothing
    }

    if (options.trackMarketing) {
      return () =>
        dispatchGtmEvent<GTMEventGlycerinMarketingClick>({
          event: 'MarketingClick',
          MarketingClickData: {
            marketingType:
              adType && adId ? GlycerinMarketingType.AD : GlycerinMarketingType.CAMPAIGN,
            ...(campaignType && { campaignType }),
            ...(campaignId && { campaignId }),
            ...(adId && { adId }),
            ...(adType && { adType }),
            category,
            detail,
            name,
            label: language || defaultLanguage,
            ...(targetUrl && { target: targetUrl }),
          },
        });
    }
    if (campaignType) {
      return () =>
        dispatchGtmEvent<GTMEventGlycerinCampaignClick>({
          event: 'CampaignClick',
          CampaignClickData: {
            campaignType,
            category,
            ...(campaignId && { campaignId }),
            ...(detail && { detail }),
            ...(name && { name }),
            label: language || defaultLanguage,
            ...(targetUrl && { target: targetUrl }),
          },
        });
    }
  }
  return () => {};
};

const executeDisplayTracking = (
  data: GlycerinCampaignTracking,
  target: string | undefined,
  dispatchGtmEvent: <T extends GenericTrackingEvent<string | {}>>(event: T) => void,
) => {
  const { campaignType, category, campaignId, name } = generateFinalTrackingData(data, target);
  if (campaignType && category) {
    dispatchGtmEvent<GTMEventGlycerinDisplayCampaign>({
      event: 'DisplayCampaign',
      DisplayCampaignData: {
        campaignType,
        ...(campaignId && { campaignId }),
        ...(name && { name }),
        category,
      },
    });
  }
};

const executeDisplayTrackingMarketing = (
  data: GlycerinCampaignTracking,
  target: string | undefined,
  dispatchGtmEvent: <T extends GenericTrackingEvent<string | {}>>(event: T) => void,
) => {
  const { category, name, adId, adType, campaignId, campaignType, detail } =
    generateFinalTrackingData(data, target);
  if (category) {
    dispatchGtmEvent<GTMEventGlycerinDisplayMarketing>({
      event: 'DisplayMarketing',
      DisplayMarketingData: {
        marketingType: adId && adType ? GlycerinMarketingType.AD : GlycerinMarketingType.CAMPAIGN,
        ...(adType && { adType }),
        ...(adId && { adId }),
        ...(campaignId && { campaignId }),
        ...(campaignType && { campaignType }),
        detail,
        name,
        category,
      },
    });
  }
};

/**
 * execute glycerin tracking for campaign display and click on viewport
 *
 * @param object glycerin tracking information
 * @param RefObject<Element> the component reference to observe
 * @param string the click target
 * @param boolean this myterious parameter is used to be able to conditionally track on load or on intersect
 * @returns function to execute the dispatcher on click
 */
const useTrackCampaign = (
  data: GlycerinCampaignTracking,
  componentReference: RefObject<Element | null>,
  target?: string,
  trackOnLoad = false,
) => {
  const dispatchGtmEvent = useTracking();

  useIntersectionObserver(componentReference, {
    intersectOnce: true,
    threshold: 1,
    callback: (isIntersecting) => {
      if (!isIntersecting || trackOnLoad) {
        return;
      }
      // intersectOnce is set to true, so this callback will only be called once
      executeDisplayTracking(data, target, dispatchGtmEvent);
    },
  });

  return { trackClick: useReturnOutput(data, { target }) };
};

/**
 * execute glycerin tracking for marketing display and click on viewport
 *
 * @param object glycerin tracking information
 * @param RefObject<Element> the component reference to observe
 * @param string the click target
 * @returns function to execute the dispatcher on click
 */
const useTrackMarketing = (
  data: GlycerinCampaignTracking,
  componentReference: RefObject<Element | null>,
  target?: string,
  trackOnLoad = false,
) => {
  const dispatchGtmEvent = useTracking();

  useIntersectionObserver(componentReference, {
    intersectOnce: true,
    threshold: 1,
    callback: (isIntersecting) => {
      if (!isIntersecting || trackOnLoad) {
        return;
      }
      // intersectOnce is set to true, so this callback will only be called once
      executeDisplayTrackingMarketing(data, target, dispatchGtmEvent);
    },
  });

  return { trackClick: useReturnOutput(data, { target, trackMarketing: true }) };
};

/**
 * execute glycerin tracking for campaign display and click
 *
 * @param object glycerin tracking information
 * @param string the click target
 * @param boolean this myterious parameter is used to be able to conditionally track on load or on intersect
 * @returns function to execute the dispatcher on click
 */
const useTrackCampaignOnLoad = (data: GlycerinCampaignTracking, target?: string) => {
  const dispatchGtmEvent = useTracking();

  return {
    trackDisplay: () => executeDisplayTracking(data, target, dispatchGtmEvent),
    trackClick: useReturnOutput(data, { target }),
  };
};

/**
 * execute glycerin tracking for marketing display and click
 *
 * @param object glycerin tracking information
 * @param string the click target
 * @returns function to execute the dispatcher on click
 */
const useTrackMarketingOnLoad = (data: GlycerinCampaignTracking, target?: string) => {
  const dispatchGtmEvent = useTracking();

  return {
    trackDisplay: () => executeDisplayTrackingMarketing(data, target, dispatchGtmEvent),
    trackClick: useReturnOutput(data, { target, trackMarketing: true }),
  };
};

export { useTrackCampaign, useTrackCampaignOnLoad, useTrackMarketing, useTrackMarketingOnLoad };
